import {
  showPlaybackDiagnosis,
  showPlaybackStatusNotice,
} from '@/lib/PlaybackDiagnosis';

/**
 * 首頁與試聽播放器共用的播放恢復流程。
 *
 * Learning.vue 因為還包含觀看進度、筆記與切課商業邏輯，所以保留自己的實作；
 * 其他播放器則引用此 mixin，確保都有相同的背景恢復、卡頓判斷及資源釋放能力。
 *
 * 核心原則：
 * 1. 短暫網路緩衝只提示，不立刻重建播放器。
 * 2. 先嘗試低干預恢復，確認媒體資源真的失效才完整重建。
 * 3. 區分「使用者主動暫停」與「瀏覽器被迫停止」，避免誤自動播放。
 */

// 卡住 5 秒才提示，避免一般 HLS 切片銜接造成訊息閃爍。
const PLAYBACK_NETWORK_NOTICE_DELAY = 5000;
// 卡住 20 秒才喚醒既有 HLS 載入，期間不重新取得授權或重建課程。
const PLAYBACK_LIGHT_RECOVERY_DELAY = 20000;
// 輕量恢復至少相隔 30 秒，避免弱網路下反覆呼叫 startLoad。
const PLAYBACK_LIGHT_RECOVERY_COOLDOWN = 30000;
// manifest 初始化只重試兩次，防止錯誤來源下無限建立播放器。
const MAX_HLS_INITIALIZATION_RETRIES = 2;
const HLS_INITIALIZATION_RETRY_DELAYS = [1000, 2500];

export default {
  data() {
    return {
      // 播放意圖：瀏覽器暫停事件不一定代表使用者真的按下暫停。
      playbackIntended: false,
      playbackHasError: false,

      // visibilitychange / emptied 的前景恢復狀態。
      playbackWasPlaying: false,
      playbackMediaWasEmptied: false,
      playbackIgnoreEmptiedUntil: 0,
      playbackVisibilityTimer: null,
      playbackProgressTimer: null,

      // 持續卡頓偵測、提示與低干預 HLS 恢復。
      playbackStallTimer: null,
      playbackNetworkNoticeTimer: null,
      playbackNetworkNoticeInstance: null,
      playbackNetworkNoticeCooldownUntil: 0,
      playbackProgressWatchTimer: null,
      playbackProgressWatchLastTime: 0,
      playbackProgressWatchStalledSince: null,
      playbackLightRecoveryTimer: null,
      playbackLastLightRecoveryAt: 0,
      playbackPauseIntentTimer: null,

      // 完整重建、診斷與 HLS 初始化有限重試。
      isPlaybackRecovering: false,
      isPlaybackCleaningUp: false,
      playbackResumeAfterRecovery: false,
      playbackLastRecoveryAt: 0,
      playbackFailureTimer: null,
      playbackDiagnosisShown: false,
      playbackHlsInitializationRetryCount: 0,
      playbackHlsInitializationRetryTimer: null,
    };
  },
  mounted() {
    // 所有使用此 mixin 的播放器都能處理分頁或 App 切回前景的情境。
    document.addEventListener('visibilitychange', this.handlePlaybackVisibilityChange);
  },
  beforeDestroy() {
    // 元件離開時同步清除 document 事件、video 事件及所有非同步計時器。
    document.removeEventListener('visibilitychange', this.handlePlaybackVisibilityChange);
    this.clearPlaybackRecoveryTimers();
    this.removePlaybackRecoveryEvents();
  },
  methods: {
    /**
     * iPhone／iPad 即使回報支援 MSE，仍優先使用 WebKit 原生 HLS。
     * 也涵蓋 iPadOS 使用桌面版 user agent（MacIntel + 觸控）的情況。
     */
    shouldUseNativeHls(video = this.getPlaybackVideo()) {
      if (!video || typeof video.canPlayType !== 'function') return false;

      const userAgent = navigator.userAgent || '';
      const platform = navigator.platform || '';
      const isIOS = /iPad|iPhone|iPod/i.test(userAgent)
        || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (!isIOS) return false;

      return !!(
        video.canPlayType('application/vnd.apple.mpegurl')
        || video.canPlayType('application/x-mpegURL')
      );
    },

    /**
     * Plyr 會把使用者選擇的倍速存進 localStorage.plyr.speed。
     * 原生 HLS 切換來源前先讀出來，避免 iOS 呼叫 media.load() 時把倍速重設成 1。
     */
    getStoredPlaybackSpeed() {
      const fallbackSpeed = Number(this.options.speed.selected) || 1;

      try {
        const storedOptions = JSON.parse(window.localStorage.getItem('plyr') || '{}');
        const storedSpeed = Number(storedOptions.speed);
        const supportedSpeeds = this.options.speed.options.map((speed) => Number(speed));
        return Number.isFinite(storedSpeed) && supportedSpeeds.includes(storedSpeed)
          ? storedSpeed
          : fallbackSpeed;
      } catch (error) {
        return fallbackSpeed;
      }
    },

    /**
     * player.source 會重建原生 video；等新來源 metadata 就緒後再套回切換前的倍速。
     * player identity 檢查可避免舊影片的延遲事件誤改到下一個播放器。
     */
    restorePlaybackSpeedAfterSourceChange(player, playbackSpeed) {
      if (!player || !Number.isFinite(playbackSpeed)) return;
      const targetPlayer = player;

      const applyPlaybackSpeed = () => {
        if (this.player !== targetPlayer || !targetPlayer.media) return;
        targetPlayer.speed = playbackSpeed;
      };

      if (targetPlayer.media && targetPlayer.media.readyState >= 1) {
        setTimeout(applyPlaybackSpeed, 0);
      } else {
        targetPlayer.once('loadedmetadata', applyPlaybackSpeed);
      }
    },

    /**
     * 找到這個元件真正使用的 video。
     * 優先從元件 DOM 尋找，再退回 Plyr media 或 previewVideo ref，
     * 是為了相容首頁播放器、試聽 Modal 等不同模板結構。
     */
    getPlaybackVideo() {
      const currentVideo = this.$el && this.$el.querySelector
        ? this.$el.querySelector('video')
        : null;
      if (currentVideo) return currentVideo;
      if (this.player && this.player.media) return this.player.media;
      return this.$refs.previewVideo || null;
    },

    // 專案目前每個畫面只會有一個 HLS instance，因此沿用 window.hls。
    getPlaybackHls() {
      return window.hls || null;
    },

    // 預覽播放器也必須清掉原生 video 的 src，否則關閉或換片後仍可能保留舊解碼管線。
    resetPlaybackMediaElement(video) {
      if (!video) return;

      const mediaElement = video;
      this.playbackIgnoreEmptiedUntil = Date.now() + 1000;
      try {
        mediaElement.pause();
        if ('srcObject' in mediaElement && mediaElement.srcObject) {
          mediaElement.srcObject = null;
        }
        mediaElement.removeAttribute('src');
        Array.from(mediaElement.querySelectorAll('source')).forEach((source) => {
          if (source.parentNode) source.parentNode.removeChild(source);
        });
        mediaElement.load();
      } catch (error) {
        // Plyr destroy 可能已替換此節點；呼叫端仍會再重置畫面上目前的 video。
      }
    },

    // ==================== 計時器與前景恢復 ====================
    /**
     * 集中清除恢復流程建立的 timer 與提示視窗。
     * 切片、關閉 Modal、切到背景或銷毀元件時都會呼叫，避免舊 callback 操作新播放器。
     */
    clearPlaybackRecoveryTimers() {
      this.stopPlaybackProgressWatch();

      if (this.playbackVisibilityTimer) {
        clearTimeout(this.playbackVisibilityTimer);
        this.playbackVisibilityTimer = null;
      }
      if (this.playbackProgressTimer) {
        clearTimeout(this.playbackProgressTimer);
        this.playbackProgressTimer = null;
      }
      if (this.playbackStallTimer) {
        clearTimeout(this.playbackStallTimer);
        this.playbackStallTimer = null;
      }
      if (this.playbackNetworkNoticeTimer) {
        clearTimeout(this.playbackNetworkNoticeTimer);
        this.playbackNetworkNoticeTimer = null;
      }
      if (this.playbackNetworkNoticeInstance) {
        this.playbackNetworkNoticeInstance.close();
        this.playbackNetworkNoticeInstance = null;
      }
      if (this.playbackFailureTimer) {
        clearTimeout(this.playbackFailureTimer);
        this.playbackFailureTimer = null;
      }
      if (this.playbackLightRecoveryTimer) {
        clearTimeout(this.playbackLightRecoveryTimer);
        this.playbackLightRecoveryTimer = null;
      }
      if (this.playbackHlsInitializationRetryTimer) {
        clearTimeout(this.playbackHlsInitializationRetryTimer);
        this.playbackHlsInitializationRetryTimer = null;
      }
    },

    /**
     * 頁面進入背景時只記住原本是否正在播放，不立即 reload。
     * 回到前景後保留 1 秒讓瀏覽器自行恢復，只有原本想播放時才進一步檢查。
     */
    handlePlaybackVisibilityChange() {
      const video = this.getPlaybackVideo();
      if (!video || !this.player || this.isPlaybackCleaningUp) return;

      if (document.hidden) {
        this.clearPlaybackRecoveryTimers();
        this.playbackWasPlaying = !video.paused && !video.ended;
        return;
      }

      if (
        this.playbackWasPlaying
        || (this.playbackMediaWasEmptied && this.playbackIntended)
      ) {
        if (this.playbackVisibilityTimer) clearTimeout(this.playbackVisibilityTimer);
        this.playbackVisibilityTimer = setTimeout(() => {
          this.playbackVisibilityTimer = null;
          this.checkPlaybackAfterVisibility();
        }, 1000);
      }
    },

    /**
     * 前景寬限期結束後分兩段判斷：
     * 1. readyState < 3 代表目前資料不足，直接進入恢復。
     * 2. 狀態看似正常時再等 500ms，確認 currentTime 是否真的前進。
     */
    checkPlaybackAfterVisibility() {
      const video = this.getPlaybackVideo();
      if (
        document.hidden
        || !video
        || !this.player
        || (
          !this.playbackWasPlaying
          && !(this.playbackMediaWasEmptied && this.playbackIntended)
        )
        || this.isPlaybackCleaningUp
        || this.isPlaybackRecovering
      ) return;

      if (video.readyState < 3 || video.ended) {
        this.recoverPlayback('visibility');
        return;
      }

      const startTime = video.currentTime;
      if (this.playbackProgressTimer) clearTimeout(this.playbackProgressTimer);
      this.playbackProgressTimer = setTimeout(() => {
        this.playbackProgressTimer = null;
        const currentVideo = this.getPlaybackVideo();
        if (
          document.hidden
          || !currentVideo
          || !this.player
          || (
            !this.playbackWasPlaying
            && !(this.playbackMediaWasEmptied && this.playbackIntended)
          )
          || this.isPlaybackCleaningUp
          || this.isPlaybackRecovering
        ) return;

        const hasProgressed = currentVideo.currentTime > startTime + 0.1;
        if (
          currentVideo.readyState < 3
          || currentVideo.paused
          || currentVideo.ended
          || !hasProgressed
        ) {
          this.recoverPlayback('visibility');
        } else {
          this.playbackMediaWasEmptied = false;
        }
      }, 500);
    },

    /**
     * emptied 是瀏覽器真的清空媒體資源時會送出的訊號。
     * 若播放原本仍在進行，就沿用前景恢復檢查；使用者已暫停時則不擅自播放。
     */
    handlePlaybackEmptied() {
      // 主動釋放媒體時 load() 也會送出 emptied，不能把正常 cleanup 誤判成故障。
      if (
        Date.now() < this.playbackIgnoreEmptiedUntil
        || this.isPlaybackCleaningUp
        || !this.player
      ) return;

      this.playbackMediaWasEmptied = true;
      const playbackWasActive = this.playbackIntended
        || this.playbackWasPlaying
        || (this.player.playing && !this.player.paused);
      if (playbackWasActive && !document.hidden) {
        this.playbackWasPlaying = true;
        if (this.playbackVisibilityTimer) clearTimeout(this.playbackVisibilityTimer);
        this.playbackVisibilityTimer = setTimeout(() => {
          this.playbackVisibilityTimer = null;
          this.checkPlaybackAfterVisibility();
        }, 1000);
      }
    },

    // ==================== 緩衝偵測與低干預恢復 ====================
    /**
     * waiting/stalled 通常只是網路緩衝，因此只安排提示和輕量恢復。
     * media-error 等明確錯誤才在持續 5 秒未前進後呼叫 recoverPlayback。
     */
    schedulePlaybackRecovery(reason = 'stall') {
      if (reason === 'waiting' || reason === 'stalled') {
        this.schedulePlaybackNetworkNotice(reason);
        this.schedulePlaybackLightRecovery();
        return;
      }

      const video = this.getPlaybackVideo();
      if (
        !video
        || !this.player
        || document.hidden
        || this.isPlaybackCleaningUp
        || this.isPlaybackRecovering
        || (video.paused && !this.playbackIntended)
        || video.ended
      ) return;

      if (this.playbackStallTimer) clearTimeout(this.playbackStallTimer);
      const startTime = video.currentTime;
      this.playbackStallTimer = setTimeout(() => {
        this.playbackStallTimer = null;
        const currentVideo = this.getPlaybackVideo();
        if (
          !currentVideo
          || !this.player
          || document.hidden
          || this.isPlaybackCleaningUp
          || this.isPlaybackRecovering
          || currentVideo.ended
        ) return;

        const hasProgressed = currentVideo.currentTime > startTime + 0.1;
        if (
          currentVideo.readyState < 3
          || (currentVideo.paused && this.playbackIntended)
          || !hasProgressed
        ) {
          this.recoverPlayback(reason);
        }
      }, 5000);
    },

    handlePlaybackWaiting() {
      this.schedulePlaybackRecovery('waiting');
    },

    handlePlaybackStalled() {
      this.schedulePlaybackRecovery('stalled');
    },

    // 事件發生後再觀察 5 秒；影片已自行繼續播放就完全不顯示提示。
    schedulePlaybackNetworkNotice(reason = 'waiting') {
      const video = this.getPlaybackVideo();
      if (
        !video
        || document.hidden
        || this.isPlaybackCleaningUp
        || this.isPlaybackRecovering
        || (video.paused && !this.playbackIntended)
        || video.ended
      ) return;

      if (this.playbackNetworkNoticeTimer) clearTimeout(this.playbackNetworkNoticeTimer);
      const startTime = video.currentTime;
      this.playbackNetworkNoticeTimer = setTimeout(() => {
        this.playbackNetworkNoticeTimer = null;
        const currentVideo = this.getPlaybackVideo();
        if (
          !currentVideo
          || document.hidden
          || this.isPlaybackCleaningUp
          || (currentVideo.paused && !this.playbackIntended)
          || currentVideo.ended
        ) return;

        const hasProgressed = currentVideo.currentTime > startTime + 0.1;
        if (!hasProgressed) {
          this.showPlaybackNetworkNoticeIfAllowed(currentVideo, reason);
        }
      }, PLAYBACK_NETWORK_NOTICE_DELAY);
    },

    /**
     * 長時間沒有進度時，重新 attach 既有 media 並從目前秒數 startLoad。
     * 此步驟不 destroy Plyr、不重新載入課程，也不呼叫 GetVideoCookies，
     * 所以弱網路下不會出現使用者看到的整頁重載或反覆轉圈。
     */
    schedulePlaybackLightRecovery() {
      const video = this.getPlaybackVideo();
      const hls = this.getPlaybackHls();
      if (
        !video
        || !hls
        || document.hidden
        || this.isPlaybackCleaningUp
        || this.isPlaybackRecovering
        || (!this.playbackIntended && video.paused)
        || video.ended
        || this.playbackLightRecoveryTimer
        || Date.now() - this.playbackLastLightRecoveryAt
          < PLAYBACK_LIGHT_RECOVERY_COOLDOWN
      ) return;

      const startTime = video.currentTime;
      this.playbackLightRecoveryTimer = setTimeout(() => {
        this.playbackLightRecoveryTimer = null;
        const currentVideo = this.getPlaybackVideo();
        const currentHls = this.getPlaybackHls();
        if (
          !currentVideo
          || !currentHls
          || document.hidden
          || this.isPlaybackCleaningUp
          || this.isPlaybackRecovering
          || (!this.playbackIntended && currentVideo.paused)
          || currentVideo.ended
          || currentVideo.currentTime > startTime + 0.1
        ) return;

        this.playbackLastLightRecoveryAt = Date.now();
        try {
          if (!currentHls.media) currentHls.attachMedia(currentVideo);
          currentHls.startLoad(currentVideo.currentTime);
        } catch (error) {
          // 保留原播放器，交由既有錯誤與提示流程處理。
        }
      }, PLAYBACK_LIGHT_RECOVERY_DELAY);
    },

    /**
     * playing 後每秒檢查 currentTime 是否前進。
     * 不使用高頻 timeupdate，能降低長時間播放時的事件負擔，
     * 同時捕捉「沒有 error 事件，但畫面與聲音都停住」的靜默故障。
     */
    startPlaybackProgressWatch() {
      const video = this.getPlaybackVideo();
      if (!video || this.playbackProgressWatchTimer) return;

      this.playbackProgressWatchLastTime = video.currentTime;
      this.playbackProgressWatchStalledSince = null;
      this.playbackProgressWatchTimer = setInterval(() => {
        const currentVideo = this.getPlaybackVideo();
        if (
          !currentVideo
          || document.hidden
          || this.isPlaybackCleaningUp
          || (currentVideo.paused && !this.playbackIntended)
          || currentVideo.ended
        ) {
          this.stopPlaybackProgressWatch();
          return;
        }

        const hasProgressed = currentVideo.currentTime
          > this.playbackProgressWatchLastTime + 0.1;
        if (hasProgressed) {
          this.playbackProgressWatchLastTime = currentVideo.currentTime;
          this.playbackProgressWatchStalledSince = null;
          return;
        }

        if (this.playbackProgressWatchStalledSince === null) {
          this.playbackProgressWatchStalledSince = Date.now();
          return;
        }

        if (
          Date.now() - this.playbackProgressWatchStalledSince >= PLAYBACK_NETWORK_NOTICE_DELAY
        ) {
          this.schedulePlaybackLightRecovery();
          if (!this.playbackNetworkNoticeInstance) {
            this.showPlaybackNetworkNoticeIfAllowed(currentVideo, 'waiting');
          }
        }
      }, 1000);
    },

    // 使用者暫停、影片結束、切到背景或清理播放器時停止輪詢。
    stopPlaybackProgressWatch() {
      if (this.playbackProgressWatchTimer) {
        clearInterval(this.playbackProgressWatchTimer);
        this.playbackProgressWatchTimer = null;
      }
      this.playbackProgressWatchLastTime = 0;
      this.playbackProgressWatchStalledSince = null;
    },

    // 同時間只保留一則提示，並用冷卻時間避免弱網路下重複干擾使用者。
    showPlaybackNetworkNoticeIfAllowed(video, reason = 'waiting') {
      if (
        this.playbackNetworkNoticeInstance
        || Date.now() < this.playbackNetworkNoticeCooldownUntil
      ) return;

      this.playbackNetworkNoticeCooldownUntil = Date.now() + 30000;
      this.playbackNetworkNoticeInstance = showPlaybackStatusNotice(this, {
        video: video || this.getPlaybackVideo(),
        reason,
        mediaWasEmptied: this.playbackMediaWasEmptied,
      });
    },

    handlePlaybackError() {
      this.playbackHasError = true;
      this.schedulePlaybackRecovery('media-error');
    },

    // ==================== HLS 初始化錯誤有限重試 ====================
    // manifest 成功後歸零，下一堂課若初始化失敗仍可重新獲得兩次機會。
    resetPlaybackHlsInitializationRetry() {
      if (this.playbackHlsInitializationRetryTimer) {
        clearTimeout(this.playbackHlsInitializationRetryTimer);
        this.playbackHlsInitializationRetryTimer = null;
      }
      this.playbackHlsInitializationRetryCount = 0;
    },

    markPlaybackHlsInitialized() {
      this.resetPlaybackHlsInitializationRetry();
      this.playbackHasError = false;
    },

    /**
     * HLS 在 Plyr 尚未建立前就失敗時，無法走一般播放恢復，
     * 因此以 1 秒、2.5 秒的有限退避重新建立；切片後舊重試會因 URL 不同而失效。
     */
    schedulePlaybackHlsInitializationRetry(hls, reason) {
      if (
        !hls
        || window.hls !== hls
        || this.isPlaybackCleaningUp
        || this.playbackHlsInitializationRetryTimer
      ) return;

      if (
        this.playbackHlsInitializationRetryCount
        >= MAX_HLS_INITIALIZATION_RETRIES
      ) {
        this.showPlaybackDiagnosis(reason);
        return;
      }

      const retryVideoUrl = this.currentVideoUrl;
      const retryDelay = HLS_INITIALIZATION_RETRY_DELAYS[
        this.playbackHlsInitializationRetryCount
      ];
      this.playbackHlsInitializationRetryCount += 1;
      this.playbackHlsInitializationRetryTimer = setTimeout(async () => {
        this.playbackHlsInitializationRetryTimer = null;
        if (
          window.hls !== hls
          || this.isPlaybackCleaningUp
          || this.currentVideoUrl !== retryVideoUrl
        ) return;

        if (typeof this.rebuildPlaybackPlayer === 'function') {
          try {
            this.playbackHasError = false;
            await this.rebuildPlaybackPlayer({
              currentTime: Number.isFinite(this.currentTime) ? this.currentTime : 0,
              resume: false,
              reason,
            });
          } catch (error) {
            this.playbackHasError = true;
            this.showPlaybackDiagnosis(reason);
          }
        }
      }, retryDelay);
    },

    // 已有 Plyr 時走媒體恢復；manifest 階段尚無 Plyr 時走初始化重試。
    handlePlaybackHlsError(reason, hls = this.getPlaybackHls()) {
      this.playbackHasError = true;
      if (this.player) {
        this.schedulePlaybackRecovery(reason);
      } else {
        this.schedulePlaybackHlsInitializationRetry(hls, reason);
      }
    },

    // ==================== 使用者提示與播放意圖 ====================
    // PlaybackDiagnosis 會依 readyState、buffer 與錯誤原因選擇較合適的使用者訊息。
    showPlaybackDiagnosis(
      reason,
      video = this.getPlaybackVideo(),
      mediaWasEmptied = this.playbackMediaWasEmptied,
    ) {
      showPlaybackDiagnosis(this, {
        video,
        reason,
        mediaWasEmptied,
      });
    },

    // 完整重建後再給 10 秒下載資料；狀態已健康就不顯示失敗訊息。
    schedulePlaybackDiagnosis(
      reason,
      mediaWasEmptied = this.playbackMediaWasEmptied,
    ) {
      if (this.playbackFailureTimer) clearTimeout(this.playbackFailureTimer);
      this.playbackFailureTimer = setTimeout(() => {
        this.playbackFailureTimer = null;
        const video = this.getPlaybackVideo();
        const isHealthy = video
          && video.readyState >= 3
          && !video.ended;
        if (!isHealthy) this.showPlaybackDiagnosis(reason, video, mediaWasEmptied);
      // 完整重建後，HLS 可能仍在下載 manifest/segment；低速網路不應在 10 秒內被誤判為失敗
      }, 10000);
    },

    // playing 代表媒體已實際開始輸出，此時清除舊提示與待執行的恢復工作。
    handlePlaybackHealthy() {
      this.playbackIntended = true;
      this.playbackHasError = false;
      if (this.playbackPauseIntentTimer) {
        clearTimeout(this.playbackPauseIntentTimer);
        this.playbackPauseIntentTimer = null;
      }
      if (this.playbackStallTimer) {
        clearTimeout(this.playbackStallTimer);
        this.playbackStallTimer = null;
      }
      if (this.playbackNetworkNoticeTimer) {
        clearTimeout(this.playbackNetworkNoticeTimer);
        this.playbackNetworkNoticeTimer = null;
      }
      if (this.playbackFailureTimer) {
        clearTimeout(this.playbackFailureTimer);
        this.playbackFailureTimer = null;
      }
      if (this.playbackLightRecoveryTimer) {
        clearTimeout(this.playbackLightRecoveryTimer);
        this.playbackLightRecoveryTimer = null;
      }
      if (this.playbackNetworkNoticeInstance) {
        this.playbackNetworkNoticeInstance.close();
        this.playbackNetworkNoticeInstance = null;
      }
      this.startPlaybackProgressWatch();
    },

    // 記錄使用者或程式已要求播放，供後續判斷暫停是否屬於異常。
    handlePlaybackPlay() {
      if (this.playbackPauseIntentTimer) {
        clearTimeout(this.playbackPauseIntentTimer);
        this.playbackPauseIntentTimer = null;
      }
      this.playbackIntended = true;
      this.playbackWasPlaying = true;
      this.playbackHasError = false;
    },

    /**
     * pause 延遲 100ms 才判定為使用者意圖。
     * 若同時伴隨 error、emptied 或恢復流程，就不把 playbackIntended 清成 false，
     * 否則播放器異常暫停後，系統會誤以為使用者不想繼續播放。
     */
    handlePlaybackPause() {
      if (this.playbackPauseIntentTimer) clearTimeout(this.playbackPauseIntentTimer);
      const pausedVideo = this.getPlaybackVideo();
      this.playbackPauseIntentTimer = setTimeout(() => {
        this.playbackPauseIntentTimer = null;
        if (
          !pausedVideo
          || pausedVideo !== this.getPlaybackVideo()
          || this.isPlaybackCleaningUp
          || this.isPlaybackRecovering
          || this.playbackHasError
          || this.playbackMediaWasEmptied
          || pausedVideo.error
          || !pausedVideo.paused
          || pausedVideo.ended
        ) return;

        this.playbackIntended = false;
        if (!document.hidden) this.playbackWasPlaying = false;
        this.stopPlaybackProgressWatch();
        if (this.playbackLightRecoveryTimer) {
          clearTimeout(this.playbackLightRecoveryTimer);
          this.playbackLightRecoveryTimer = null;
        }
      }, 100);
    },

    // 自然播放結束不需要恢復，並清除所有計時器與播放意圖。
    handlePlaybackEnded() {
      this.playbackIntended = false;
      this.playbackHasError = false;
      this.playbackWasPlaying = false;
      this.playbackMediaWasEmptied = false;
      this.clearPlaybackRecoveryTimers();
    },

    // 重建完成後等 canplay 再續播，避免 metadata 尚未就緒時直接 play 失敗。
    handlePlaybackCanPlay() {
      if (this.playbackResumeAfterRecovery && this.player && !this.player.ended) {
        this.playbackResumeAfterRecovery = false;
        this.playbackIntended = true;
        this.player.play().catch(() => {
          // 自動播放受瀏覽器限制時，保留使用者手動播放的選項
        });
      }
    },

    // ==================== 播放器事件生命週期 ====================
    /**
     * Plyr 事件負責使用者操作；原生 video 事件負責底層媒體狀態。
     * 每次建立播放器前先解除舊監聽，避免切片後同一事件被處理多次。
     */
    setupPlaybackRecoveryEvents() {
      if (!this.player) return;

      this.removePlaybackRecoveryEvents();
      this.player.on('error', this.handlePlaybackError);
      this.player.on('play', this.handlePlaybackPlay);
      this.player.on('pause', this.handlePlaybackPause);
      this.player.on('ended', this.handlePlaybackEnded);
      this.player.on('playing', this.handlePlaybackHealthy);
      const video = this.getPlaybackVideo();
      if (!video) return;

      video.addEventListener('canplay', this.handlePlaybackCanPlay);
      video.addEventListener('emptied', this.handlePlaybackEmptied);
      video.addEventListener('waiting', this.handlePlaybackWaiting);
      video.addEventListener('stalled', this.handlePlaybackStalled);
      video.addEventListener('playing', this.handlePlaybackHealthy);
    },

    // 必須使用與 addEventListener/on 相同的方法 reference 才能正確解除。
    removePlaybackRecoveryEvents() {
      if (this.player) {
        this.player.off('error', this.handlePlaybackError);
        this.player.off('play', this.handlePlaybackPlay);
        this.player.off('pause', this.handlePlaybackPause);
        this.player.off('ended', this.handlePlaybackEnded);
        this.player.off('playing', this.handlePlaybackHealthy);
      }

      const video = this.getPlaybackVideo();
      if (!video) return;

      video.removeEventListener('canplay', this.handlePlaybackCanPlay);
      video.removeEventListener('emptied', this.handlePlaybackEmptied);
      video.removeEventListener('waiting', this.handlePlaybackWaiting);
      video.removeEventListener('stalled', this.handlePlaybackStalled);
      video.removeEventListener('playing', this.handlePlaybackHealthy);
    },

    // 元件自己的 destroyPlayer 前，先結束 mixin 所有狀態與非同步工作。
    cleanupPlaybackRecovery() {
      // 此方法只清事件與恢復狀態；播放器元件的 destroyPlayer() 會接著釋放實際媒體。
      this.isPlaybackCleaningUp = true;
      this.clearPlaybackRecoveryTimers();
      if (this.playbackPauseIntentTimer) {
        clearTimeout(this.playbackPauseIntentTimer);
        this.playbackPauseIntentTimer = null;
      }
      this.removePlaybackRecoveryEvents();
      this.playbackIntended = false;
      this.playbackHasError = false;
      this.playbackWasPlaying = false;
      this.playbackMediaWasEmptied = false;
      this.playbackResumeAfterRecovery = false;
      this.isPlaybackCleaningUp = false;
    },

    // 只有媒體資源被回收或 media error 才允許完整重建；一般網路緩衝只提示並等待。
    /**
     * 恢復順序：
     * 1. 保留 currentTime 與原本播放意圖。
     * 2. 先用 attachMedia/startLoad、recoverMediaError 或原生 load() 輕量恢復。
     * 3. 等待後確認 readyState、paused 與 currentTime 是否真的恢復。
     * 4. 只有 visibility/media-error 仍失敗時才呼叫元件提供的 rebuildPlaybackPlayer。
     */
    async recoverPlayback(reason = 'stall') {
      const video = this.getPlaybackVideo();
      if (
        !video
        || !this.player
        || this.isPlaybackCleaningUp
        || this.isPlaybackRecovering
      ) return;

      const playbackWasActive = this.playbackIntended
        || this.playbackWasPlaying
        || (!video.paused && !video.ended);
      if (!playbackWasActive) return;
      if (Date.now() - this.playbackLastRecoveryAt < 3000) return;

      this.playbackLastRecoveryAt = Date.now();
      const recoveryTime = Number.isFinite(video.currentTime)
        ? video.currentTime
        : this.currentTime;
      const recoveryMediaWasEmptied = this.playbackMediaWasEmptied;
      this.isPlaybackRecovering = true;
      this.clearPlaybackRecoveryTimers();

      try {
        // 第一階段：保留既有播放器與授權，只重新連接／喚醒媒體管線。
        const hls = this.getPlaybackHls();
        if (hls) {
          if (hls.media !== video) hls.attachMedia(video);
          if (
            reason === 'media-error'
            && typeof hls.recoverMediaError === 'function'
          ) {
            hls.recoverMediaError();
          }
          hls.startLoad(recoveryTime);
        } else if (video.readyState === 0) {
          video.load();
          if (recoveryTime > 0) {
            try {
              video.currentTime = recoveryTime;
            } catch (error) {
              // metadata 尚未恢復時由 canplay 再套用觀看位置
            }
          }
        }

        if (this.player.paused) {
          try {
            await this.player.play();
          } catch (error) {
            // 自動播放可能被瀏覽器阻擋，後續仍可進行完整重建
          }
        }

        // 前景恢復只需短檢查；其他錯誤多給 HLS 一點重新下載時間。
        const recoveryCheckDelay = reason === 'visibility' ? 500 : 5000;
        await new Promise((resolve) => {
          setTimeout(resolve, recoveryCheckDelay);
        });

        const recoveredVideo = this.getPlaybackVideo();
        const hasProgressed = recoveredVideo
          && recoveredVideo.currentTime > recoveryTime + 0.1;
        const isRecovered = recoveredVideo
          && recoveredVideo.readyState >= 3
          && !recoveredVideo.paused
          && !recoveredVideo.ended
          && hasProgressed;

        if (isRecovered) {
          this.playbackIntended = true;
          this.playbackHasError = false;
          this.playbackMediaWasEmptied = false;
          this.playbackWasPlaying = true;
          return;
        }

        // 網路錯誤只做輕量 HLS 恢復；不要因網路不穩重新載入整個課程。
        if (reason !== 'visibility' && reason !== 'media-error') {
          this.showPlaybackDiagnosis(reason, recoveredVideo, recoveryMediaWasEmptied);
          return;
        }

        // 第二階段：只有確認底層媒體失效，才交由各播放器元件完整重建。
        if (typeof this.rebuildPlaybackPlayer === 'function') {
          this.playbackResumeAfterRecovery = playbackWasActive;
          await this.rebuildPlaybackPlayer({
            currentTime: recoveryTime,
            resume: playbackWasActive,
            reason,
          });
          this.schedulePlaybackDiagnosis(reason, recoveryMediaWasEmptied);
        }
      } catch (error) {
        this.showPlaybackDiagnosis(
          reason,
          this.getPlaybackVideo(),
          recoveryMediaWasEmptied,
        );
      } finally {
        this.isPlaybackRecovering = false;
        this.playbackMediaWasEmptied = false;
      }
    },
  },
};
